import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "admin-notice" }
const _hoisted_2 = { class: "page-header" }
const _hoisted_3 = { class: "header-actions" }
const _hoisted_4 = { class: "notice-idx" }
const _hoisted_5 = { class: "type-chips" }
const _hoisted_6 = { class: "notice-title-cell" }
const _hoisted_7 = { class: "notice-title" }
const _hoisted_8 = {
  key: 1,
  class: "file-count"
}
const _hoisted_9 = { class: "writer-info" }
const _hoisted_10 = { class: "hit-count" }
const _hoisted_11 = { class: "no-data-message" }

import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';

interface Notice {
  nc_idx: number;
  nc_type: 'ADMIN' | 'CENTER';
  nc_category: 'NOTICE' | 'UPDATE' | 'EVENT' | 'URGENT';
  nc_sisul_code: string | null;
  nc_sisul_name: string | null;
  nc_writer_name: string;
  nc_title: string;
  nc_hit: number;
  nc_is_active: string;
  nc_wdate: string;
  nc_rdate: string | null;
  file_count: number;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminNotice',
  setup(__props) {

const router = useRouter();
const store = useStore();

// 상태
const loading = ref(false);
const notices = ref<Notice[]>([]);
const pagination = ref<Pagination>({
  total: 0,
  page: 1,
  limit: 15,
  totalPages: 0
});
const searchQuery = ref('');
const filterType = ref('ALL');
const snackbar = ref({
  show: false,
  text: '',
  color: 'info'
});

// 타입 필터 옵션
const typeOptions = [
  { text: '전체', value: 'ALL' },
  { text: '관리자', value: 'ADMIN' },
  { text: '센터장', value: 'CENTER' }
];

// 테이블 헤더
const headers = [
  { title: '번호', key: 'nc_idx', sortable: false, width: '70px', align: 'center' },
  { title: '분류', key: 'nc_type', sortable: false, width: '180px' },
  { title: '제목', key: 'nc_title', sortable: false },
  { title: '작성자', key: 'nc_writer_name', sortable: false, width: '100px' },
  { title: '조회', key: 'nc_hit', sortable: true, width: '90px', align: 'center' },
  { title: '작성일', key: 'nc_wdate', sortable: true, width: '140px', align: 'center' }
];

// 필터링된 공지사항
const filteredNotices = computed(() => {
  let result = notices.value;
  
  // 타입 필터
  if (filterType.value !== 'ALL') {
    result = result.filter(n => n.nc_type === filterType.value);
  }
  
  // 검색어 필터
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(n => 
      n.nc_title?.toLowerCase().includes(query) ||
      n.nc_writer_name?.toLowerCase().includes(query)
    );
  }
  
  return result;
});

// 공지사항 목록 로드
const loadNotices = async (page = 1) => {
  loading.value = true;
  try {
    const response = await axios.get('/api/notice/list', {
      params: { page, limit: 15 },
      withCredentials: true
    });
    
    if (response.data.success) {
      notices.value = response.data.notices;
      pagination.value = response.data.pagination;
    } else {
      showSnackbar('공지사항을 불러오는데 실패했습니다.', 'error');
    }
  } catch (error: any) {
    console.error('공지사항 목록 로드 오류:', error);
    showSnackbar('서버 오류가 발생했습니다.', 'error');
  } finally {
    loading.value = false;
  }
};

// 상세 페이지로 이동
const goToDetail = (event: Event, { item }: { item: Notice }) => {
  router.push(`/admin/notice/${item.nc_idx}`);
};

// 작성 페이지로 이동
const goToWrite = () => {
  router.push('/admin/notice/write');
};

// 유틸리티 함수
const formatDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  
  return `${year}.${month}.${day} ${hours}:${minutes}`;
};

const isNew = (date: string) => {
  if (!date) return false;
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  return diff < 3 * 24 * 60 * 60 * 1000; // 3일 이내
};

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'NOTICE': return 'primary';
    case 'UPDATE': return 'info';
    case 'EVENT': return 'success';
    case 'URGENT': return 'error';
    default: return 'grey';
  }
};

const getCategoryText = (category: string) => {
  switch (category) {
    case 'NOTICE': return '공지';
    case 'UPDATE': return '업데이트';
    case 'EVENT': return '이벤트';
    case 'URGENT': return '긴급';
    default: return category;
  }
};

const showSnackbar = (text: string, color: string = 'info') => {
  snackbar.value = { show: true, text, color };
};

onMounted(() => {
  loadNotices();
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[6] || (_cache[6] = _createElementVNode("div", { class: "header-content" }, [
        _createElementVNode("h1", { class: "page-title" }, "공지사항"),
        _createElementVNode("p", { class: "page-subtitle" }, "공지사항을 관리합니다")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_v_btn, {
          color: "white",
          variant: "flat",
          onClick: goToWrite
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, { start: "" }, {
              default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                _createTextVNode("mdi-plus", -1)
              ]))]),
              _: 1
            }),
            _cache[5] || (_cache[5] = _createTextVNode(" 공지 작성 ", -1))
          ]),
          _: 1
        })
      ])
    ]),
    _createVNode(_component_v_card, {
      class: "search-card",
      elevation: "1"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card_text, { class: "pa-4" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_row, { align: "center" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, {
                  cols: "12",
                  md: "3"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_select, {
                      modelValue: filterType.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filterType).value = $event)),
                      items: typeOptions,
                      "item-title": "text",
                      "item-value": "value",
                      label: "분류",
                      variant: "outlined",
                      density: "compact",
                      "hide-details": ""
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, {
                  cols: "12",
                  md: "4"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_text_field, {
                      modelValue: searchQuery.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
                      "prepend-inner-icon": "mdi-magnify",
                      label: "검색",
                      placeholder: "제목, 작성자 검색",
                      variant: "outlined",
                      density: "compact",
                      "hide-details": "",
                      clearable: ""
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, {
                  cols: "12",
                  md: "2"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      color: "primary",
                      variant: "tonal",
                      onClick: loadNotices,
                      loading: loading.value
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, { start: "" }, {
                          default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                            _createTextVNode("mdi-refresh", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[8] || (_cache[8] = _createTextVNode(" 새로고침 ", -1))
                      ]),
                      _: 1
                    }, 8, ["loading"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_v_card, {
      class: "notice-list-card",
      elevation: "1"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_data_table, {
          headers: headers,
          items: filteredNotices.value,
          loading: loading.value,
          "items-per-page": -1,
          class: "notice-table",
          hover: "",
          "onClick:row": goToDetail
        }, {
          "item.nc_idx": _withCtx(({ item, index }) => [
            _createElementVNode("span", _hoisted_4, _toDisplayString(pagination.value.total - ((pagination.value.page - 1) * pagination.value.limit) - index), 1)
          ]),
          "item.nc_type": _withCtx(({ item }) => [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_component_v_chip, {
                color: getCategoryColor(item.nc_category),
                size: "small",
                variant: "flat",
                class: "mr-1"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(getCategoryText(item.nc_category)), 1)
                ]),
                _: 2
              }, 1032, ["color"]),
              _createVNode(_component_v_chip, {
                color: item.nc_type === 'ADMIN' ? 'grey-darken-1' : 'teal',
                size: "x-small",
                variant: "outlined"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(item.nc_type === 'ADMIN' ? '관리자' : item.nc_sisul_name || '센터'), 1)
                ]),
                _: 2
              }, 1032, ["color"])
            ])
          ]),
          "item.nc_title": _withCtx(({ item }) => [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("span", _hoisted_7, _toDisplayString(item.nc_title), 1),
              (item.file_count > 0)
                ? (_openBlock(), _createBlock(_component_v_icon, {
                    key: 0,
                    size: "small",
                    color: "grey",
                    class: "ml-1"
                  }, {
                    default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                      _createTextVNode(" mdi-attachment ", -1)
                    ]))]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (item.file_count > 0)
                ? (_openBlock(), _createElementBlock("span", _hoisted_8, "(" + _toDisplayString(item.file_count) + ")", 1))
                : _createCommentVNode("", true),
              (isNew(item.nc_wdate))
                ? (_openBlock(), _createBlock(_component_v_chip, {
                    key: 2,
                    color: "error",
                    size: "x-small",
                    class: "ml-2"
                  }, {
                    default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                      _createTextVNode("NEW", -1)
                    ]))]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ])
          ]),
          "item.nc_writer_name": _withCtx(({ item }) => [
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("span", null, _toDisplayString(item.nc_type === 'ADMIN' ? '관리자' : item.nc_sisul_name || '-'), 1)
            ])
          ]),
          "item.nc_hit": _withCtx(({ item }) => [
            _createElementVNode("span", _hoisted_10, _toDisplayString(item.nc_hit), 1)
          ]),
          "item.nc_wdate": _withCtx(({ item }) => [
            _createTextVNode(_toDisplayString(formatDate(item.nc_wdate)), 1)
          ]),
          loading: _withCtx(() => [
            _createVNode(_component_v_skeleton_loader, { type: "table-row@10" })
          ]),
          "no-data": _withCtx(() => [
            _createElementVNode("div", _hoisted_11, [
              _createVNode(_component_v_icon, {
                size: "64",
                color: "grey-lighten-1"
              }, {
                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                  _createTextVNode("mdi-bulletin-board", -1)
                ]))]),
                _: 1
              }),
              _cache[12] || (_cache[12] = _createElementVNode("p", null, "등록된 공지사항이 없습니다", -1))
            ])
          ]),
          _: 1
        }, 8, ["items", "loading"])
      ]),
      _: 1
    }),
    _createVNode(_component_v_snackbar, {
      modelValue: snackbar.value.show,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((snackbar.value.show) = $event)),
      color: snackbar.value.color,
      timeout: 3000,
      location: "top"
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          variant: "text",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (snackbar.value.show = false))
        }, {
          default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
            _createTextVNode("닫기", -1)
          ]))]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(snackbar.value.text) + " ", 1)
      ]),
      _: 1
    }, 8, ["modelValue", "color"])
  ]))
}
}

})